import React, { useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { hasTwoDecimalPlaces } from "src/stores/krediflow/Utils";
import styles from "./QuantityField.module.css";

interface IQuantityFieldProps {
    accountingRowIndex: number;
}

const QuantityFieldBase = (props: IQuantityFieldProps) => {
    const { t } = useTranslation();
    const { invoiceStore } = useContext(RootStoreContext);

    const { accountingRowIndex } = props;

    const quantityInputRef = useRef<HTMLInputElement>(null);

    const onChangeQuantityInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputQuantity = e.target.value;

            invoiceStore.updateAccountingWithQuantity(inputQuantity, accountingRowIndex);
        },
        [accountingRowIndex, invoiceStore]
    );

    const onBlurQuantityInput = useCallback(() => {
        const currentQuantity = invoiceStore.currentInvoice?.getAccountingQuantity(accountingRowIndex);
        const currentAmountNumber = Number(currentQuantity);

        let amountWithTwoDecimalPlaces = undefined;

        if (
            (currentAmountNumber ||
                (currentAmountNumber === 0 && currentQuantity?.length !== undefined && currentQuantity?.length > 1)) &&
            !hasTwoDecimalPlaces(currentAmountNumber)
        ) {
            amountWithTwoDecimalPlaces = currentAmountNumber.toFixed(2);
        }

        if (invoiceStore.currentInvoice && amountWithTwoDecimalPlaces) {
            invoiceStore.currentInvoice.setAccountingQuantity(amountWithTwoDecimalPlaces, accountingRowIndex);
        } else if (invoiceStore.currentInvoice && currentQuantity !== "" && currentAmountNumber === 0) {
            // 0, 0.0, 0.00 is a valid value for currentQuantity for mandatory quantities @see Bug 15263
            invoiceStore.currentInvoice.setAccountingQuantity(currentAmountNumber.toString(), accountingRowIndex);
        }

        if (invoiceStore.currentInvoice?.isValidatingAccountingsOnFieldLevel) {
            const accountingsSuccessfullyValidated = invoiceStore.currentInvoice.validateAccountings();

            if (accountingsSuccessfullyValidated) {
                invoiceStore.currentInvoice.setValidatingAccountingsOnFieldLevel(false);
            }
        }
    }, [invoiceStore.currentInvoice, accountingRowIndex]);

    const quantityUnit = invoiceStore.currentInvoice?.getAccountingAccount(accountingRowIndex)?.unitOfMeasure;

    const isVisible = invoiceStore.currentInvoice?.canEditAccountingQuantityField(accountingRowIndex) ?? false;

    // The unit sits next to the input, not inside the column of the neighbouring field (#24071). UI.Input owns the
    // label above the input, so the unit is centred on the input box by measurement instead of a guessed offset.
    const containerRef = useRef<HTMLDivElement>(null);
    const unitRef = useRef<HTMLSpanElement>(null);
    const [unitOffset, setUnitOffset] = useState<number>(0);

    useLayoutEffect(() => {
        const alignUnitToInput = () => {
            const input = quantityInputRef.current;
            const container = containerRef.current;
            const unit = unitRef.current;

            if (!input || !container || !unit || !isVisible) {
                return;
            }

            const inputRect = input.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            setUnitOffset(inputRect.top - containerRect.top + inputRect.height / 2 - unit.offsetHeight / 2);
        };

        alignUnitToInput();
        window.addEventListener("resize", alignUnitToInput);

        return () => window.removeEventListener("resize", alignUnitToInput);
    }, [isVisible, quantityUnit]);

    return (
        <div ref={containerRef} className={`${isVisible ? "" : "d-none"} ${styles.QuantityFieldContainer}`}>
            <div className={styles.QuantityInputWrapper}>
                <UI.Input
                    ref={quantityInputRef}
                    label={t("screens.kredi_flow.accountings.quantity").toString()}
                    type="number"
                    autoComplete="off"
                    value={invoiceStore.currentInvoice?.getAccountingQuantity(accountingRowIndex) ?? ""}
                    onChange={onChangeQuantityInput}
                    onBlur={onBlurQuantityInput}
                />
            </div>
            <span ref={unitRef} className={styles.QuantityUnit} style={{ top: unitOffset }}>
                {t(`screens.kredi_flow.accountings.quantity_unit.${quantityUnit}`)}
            </span>
        </div>
    );
};

export const QuantityField = Sentry.withProfiler(observer(QuantityFieldBase));
